import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../../api/client';

interface ApplicationRow {
  id: number;
  userId: number;
  email: string;
  companyName: string | null;
  amountRequested: number;
  purpose: string | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  submittedAt: string;
}

const STATUS_FILTERS = ['PENDING', 'APPROVED', 'REJECTED', 'ALL'] as const;

const statusColor: Record<string, string> = {
  PENDING: 'text-gold bg-gold/10',
  APPROVED: 'text-success bg-success/10',
  REJECTED: 'text-error bg-error/10',
};

export default function AdminBusinessCreditApplications() {
  const [rows, setRows] = useState<ApplicationRow[]>([]);
  const [filter, setFilter] = useState<typeof STATUS_FILTERS[number]>('PENDING');
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState<number | null>(null);
  const [approvedLimit, setApprovedLimit] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback((status: typeof STATUS_FILTERS[number]) => {
    setLoading(true);
    api
      .get('/business-finance/admin/credit-applications', { params: status === 'ALL' ? {} : { status } })
      .then((res) => setRows(res.data.items ?? []))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(filter);
  }, [filter, load]);

  const startReview = (id: number, defaultLimit: number) => {
    setActingId(actingId === id ? null : id);
    setApprovedLimit(String(defaultLimit));
    setInterestRate('');
    setRejectionReason('');
  };

  const review = async (id: number, status: 'APPROVED' | 'REJECTED') => {
    if (status === 'REJECTED' && !rejectionReason.trim()) {
      alert('A rejection reason is required');
      return;
    }
    if (status === 'APPROVED' && (!approvedLimit || Number(approvedLimit) <= 0)) {
      alert('An approved limit is required');
      return;
    }
    setSubmitting(true);
    try {
      await api.patch(`/business-finance/admin/credit-applications/${id}`, {
        status,
        approvedLimit: status === 'APPROVED' ? Number(approvedLimit) : undefined,
        interestRate: status === 'APPROVED' && interestRate ? Number(interestRate) : undefined,
        rejectionReason: status === 'REJECTED' ? rejectionReason.trim() : undefined,
      });
      setActingId(null);
      load(filter);
    } catch (e) {
      alert('Could not review application');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl font-bold text-textPrimary">Business Credit Applications</h1>
        <div className="flex gap-2">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`text-sm px-3 py-1.5 rounded-lg border transition ${
                filter === s ? 'bg-gold/10 border-gold/40 text-gold' : 'bg-surface1 border-border text-textSecondary hover:border-gold/30'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-surface1 rounded-xl border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border text-textTertiary">
            <tr>
              <th className="text-left py-3 px-4">Business</th>
              <th className="text-left py-3 px-4">Submitted</th>
              <th className="text-left py-3 px-4">Requested</th>
              <th className="text-left py-3 px-4">Purpose</th>
              <th className="text-left py-3 px-4">Status</th>
              <th className="text-left py-3 px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-8 text-textTertiary">Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-textTertiary">No applications.</td></tr>
            ) : (
              rows.map((r) => (
                <React.Fragment key={r.id}>
                  <tr className="border-b border-border/60 hover:bg-surface2/50 transition">
                    <td className="py-3 px-4 text-textPrimary">
                      {r.companyName || `Business #${r.userId}`}
                      <div className="text-textTertiary text-xs">{r.email}</div>
                    </td>
                    <td className="py-3 px-4 text-textTertiary">{new Date(r.submittedAt).toLocaleDateString()}</td>
                    <td className="py-3 px-4 text-textPrimary font-mono">${r.amountRequested.toFixed(2)}</td>
                    <td className="py-3 px-4 text-textTertiary max-w-xs">{r.purpose || '—'}</td>
                    <td className="py-3 px-4">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${statusColor[r.status]}`}>{r.status}</span>
                    </td>
                    <td className="py-3 px-4">
                      {r.status === 'PENDING' && (
                        <button
                          onClick={() => startReview(r.id, r.amountRequested)}
                          className="text-xs px-3 py-1.5 rounded-lg bg-gold/10 border border-gold/30 text-gold hover:bg-gold/20 transition"
                        >
                          Review
                        </button>
                      )}
                    </td>
                  </tr>
                  {actingId === r.id && (
                    <tr className="border-b border-border/60 bg-surface2/30">
                      <td colSpan={6} className="py-4 px-4">
                        <div className="flex gap-3 items-end flex-wrap">
                          <label className="flex flex-col gap-1">
                            <span className="text-textTertiary text-xs">Approved limit ($)</span>
                            <input
                              type="number"
                              value={approvedLimit}
                              onChange={(e) => setApprovedLimit(e.target.value)}
                              className="w-32 px-3 py-2 bg-surface1 border border-border rounded-lg text-textPrimary text-sm focus:outline-none focus:border-gold"
                            />
                          </label>
                          <label className="flex flex-col gap-1">
                            <span className="text-textTertiary text-xs">Interest rate (%)</span>
                            <input
                              type="number"
                              value={interestRate}
                              onChange={(e) => setInterestRate(e.target.value)}
                              placeholder="0"
                              className="w-28 px-3 py-2 bg-surface1 border border-border rounded-lg text-textPrimary text-sm focus:outline-none focus:border-gold"
                            />
                          </label>
                          <textarea
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            placeholder="Rejection reason (required only if rejecting)"
                            rows={2}
                            className="flex-1 min-w-[200px] px-3 py-2 bg-surface1 border border-border rounded-lg text-textPrimary text-sm focus:outline-none focus:border-gold resize-none"
                          />
                          <button
                            onClick={() => review(r.id, 'APPROVED')}
                            disabled={submitting}
                            className="text-sm px-4 py-2 rounded-lg bg-success/10 border border-success/30 text-success font-medium hover:bg-success/20 transition disabled:opacity-40"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => review(r.id, 'REJECTED')}
                            disabled={submitting}
                            className="text-sm px-4 py-2 rounded-lg bg-error/10 border border-error/30 text-error font-medium hover:bg-error/20 transition disabled:opacity-40"
                          >
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
